/**
 * Hub mock-up policy for integrator modules (`workflow.app_settings` key `hub.mockup`).
 *
 * **Single place to drive nav badges (per module)**  
 * Load the document once (for example `fetchHubMockupDocument` in {@link Layout}), then:
 * - **Clamp QC:** one nav pill when {@link hubMockupModuleBannerVisible}(`document`, `"clampqc"`) — do not duplicate
 *   badges on each tab.
 * - **MRP / manufacturing:** keep {@link hubMockupBadgeVisibleForSurface} on the relevant nav items.
 *
 * For page headers or write blocking, use `GET /api/hub/mockup?module=<key>&page=<slug>` → **`bannerMockupVisible`**
 * / **`writesBlocked`** via {@link fetchHubMockupEffective}, or derive from the same `document` with
 * {@link hubMockupBadgeVisibleForSurface} / {@link hubMockupActiveForHubPage}.
 *
 * **Semantics** (aligned with `src/hub/hub-mockup-policy.ts`):
 * - **Page-level** (`pageSlug` non-empty): surface is “mock-up” when `enabled: true` **or** `pages[pageSlug] === true`.
 * - **Module shell** (`pageSlug` omitted): {@link hubMockupModuleBannerVisible} — `enabled` **or** any `pages.*` true.
 */

import { apiFetch } from "./api";

export type HubMockupModulePayload = {
  enabled?: boolean;
  pages?: Record<string, boolean>;
};

export type HubMockupDocument = Record<string, HubMockupModulePayload>;

export async function fetchHubMockupDocument(): Promise<{ document: HubMockupDocument }> {
  return apiFetch<{ document: HubMockupDocument }>("/api/hub/mockup");
}

export async function fetchHubMockupEffective(
  moduleKey: string,
  pageKey?: string,
): Promise<{
  document: HubMockupDocument;
  writesBlocked: boolean;
  bannerMockupVisible: boolean;
}> {
  return apiFetch<{
    document: HubMockupDocument;
    writesBlocked: boolean;
    bannerMockupVisible: boolean;
  }>("/api/hub/mockup", {
    query:
      pageKey !== undefined && pageKey !== ""
        ? { module: moduleKey, page: pageKey }
        : { module: moduleKey },
  });
}

/**
 * Same rules as API `hubMockupWritesBlocked`: whole module mock-up, or the given hub **page** under
 * `module.pages.[pageSlug]` (for example `mrp` → `planning`).
 *
 * Use `pageSlug` undefined on the module home route so only **`enabled`** shows mock-up, not **`pages.*`** alone.
 */
export function hubMockupActiveForHubPage(
  document: HubMockupDocument | null | undefined,
  moduleKey: string,
  pageSlug: string | undefined,
): boolean {
  const policy = document?.[moduleKey];
  if (!policy) {
    return false;
  }
  if (policy.enabled === true) {
    return true;
  }
  const slug = typeof pageSlug === "string" ? pageSlug.trim() : "";
  if (slug !== "" && policy.pages?.[slug] === true) {
    return true;
  }
  return false;
}

/**
 * Mirrors server `hubMockupBannerVisible`: whole **`enabled`** module or any **`pages`** entry is true → show mock-up badges in navigation.
 */
export function hubMockupModuleBannerVisible(
  document: HubMockupDocument | null | undefined,
  moduleKey: string,
): boolean {
  const policy = document?.[moduleKey];
  if (!policy) {
    return false;
  }
  if (policy.enabled === true) {
    return true;
  }
  if (!policy.pages) {
    return false;
  }
  return Object.values(policy.pages).some((value) => value === true);
}

/**
 * Whether to show a hub mock-up badge for this module **surface** (nav item or page header).
 * Matches `bannerMockupVisible` from {@link fetchHubMockupEffective} for the same `moduleKey` / `pageSlug`.
 */
export function hubMockupBadgeVisibleForSurface(
  document: HubMockupDocument | null | undefined,
  moduleKey: string,
  pageSlug: string | undefined,
): boolean {
  const slug = typeof pageSlug === "string" ? pageSlug.trim() : "";
  if (slug !== "") {
    return hubMockupActiveForHubPage(document, moduleKey, slug);
  }
  return hubMockupModuleBannerVisible(document, moduleKey);
}

/** First path segment under `/mrp/` for nav labels (omit module home). */
export function mrpHubPageSlugFromNavPath(navPath: string): string | undefined {
  const trimmed = navPath.trim();
  if (!trimmed.startsWith("/mrp")) {
    return undefined;
  }
  const withoutPrefix = trimmed.replace(/^\/mrp\/?/, "").replace(/^\/?/, "");
  if (withoutPrefix === "") {
    return undefined;
  }
  const first = withoutPrefix.split("/")[0];
  return first !== "" ? first : undefined;
}
